import {
  getCommunityCommentByIdAPI,
  getCommunityCommentsAPI,
} from '@/src/apis/rest/communityComment';
import { getCommunityPostingByIdAPI } from '@/src/apis/rest/communityPosting';
import Badge from '@/src/components/atom/Badge';
import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import Table from '@/src/components/atom/Table';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import MultipleImagePreview from '@/src/components/molecule/MultipleImagePreview';
import SectionCard from '@/src/components/molecule/SectionCard';
import SectionLabel from '@/src/components/molecule/SectionLabel';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { NAV_CONST } from '@/src/constants/nav';
import { UNIT_STRINGS } from '@/src/constants/unit';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { communityPostingDetailCommentListColumn } from '@/src/pages/Community/column';
import BlindModal from '@/src/pages/Community/components/BlindModal';
import { formatter } from '@/src/utils/formatter';
import { useQuery } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import dayjs from 'dayjs';
import { useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

export default function CommunityDetailPage() {
  // util
  const { pathname } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { isOpen, openDialog, closeDialog } = useDialog({
    type: DialogType.COMMON,
  });

  // state
  const isPosting = useMemo(() => {
    return pathname.includes('posting');
  }, [pathname]);
  const gridRef = useRef<AgGridReact>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // query
  const { data: postingDetailData, refetch: refetchPostingDetailData } =
    useQuery({
      queryKey: ['community-posting-detail', id],
      queryFn: () => {
        return getCommunityPostingByIdAPI({ id: Number(id) });
      },
      enabled: !!id && isPosting,
    });
  const { data: commentDetailData, refetch: refetchCommentDetailData } =
    useQuery({
      queryKey: ['community-comment-detail', id],
      queryFn: () => {
        return getCommunityCommentByIdAPI({ id: Number(id) });
      },
      enabled: !!id && !isPosting,
    });
  const { data: communityCommentData } = useQuery({
    queryKey: [
      'community-posting-detail',
      'community-comments',
      id,
      page,
      pageSize,
    ],
    queryFn: () => {
      return getCommunityCommentsAPI({
        page,
        pageSize,
        postingId: postingDetailData?.id,
      });
    },
    enabled: !!postingDetailData,
  });

  // event
  const handlePaginationClick = (page: number, pageSize: number) => {
    setPage(page);
    setPageSize(pageSize);
  };
  const refetchDetailData = () => {
    if (isPosting) {
      refetchPostingDetailData();
    } else {
      refetchCommentDetailData();
    }
  };

  // render
  const renderCommnutyReportTypeBadge = () => {
    const target = isPosting ? postingDetailData : commentDetailData;

    switch (target?.communityReportType) {
      case 'reception':
        return <Badge badgeColor="lightYellow">신고 접수</Badge>;
      case 'blind':
        return <Badge badgeColor="lightRed">블라인드</Badge>;
      case 'common':
        return <Badge badgeColor="lightPurple">일반</Badge>;
      default:
        return '-';
    }
  };

  const renderContent = () => {
    if (isPosting) {
      return (
        <>
          <SectionCard cardTitle="게시글 정보">
            <div className="grid grid-cols-[1fr_1fr] gap-6">
              <div className="flex flex-col gap-6">
                <SectionLabel
                  label="작성 일시"
                  value={formatter.DATE(postingDetailData?.createdAt)}
                />
                <SectionLabel
                  label="상태"
                  value={renderCommnutyReportTypeBadge()}
                />
                <SectionLabel
                  label="닉네임"
                  value={postingDetailData?.customer?.nick || '-'}
                />
                <SectionLabel
                  label="게시글 카테고리"
                  value={
                    postingDetailData?.communityCategory?.categoryName || '-'
                  }
                />
                <SectionLabel
                  label="게시글 제목"
                  value={
                    <p className="whitespace-pre-wrap text-b16r text-gray6">
                      {postingDetailData?.title}
                    </p>
                  }
                />
                <SectionLabel
                  label="게시글 내용"
                  value={
                    <p className="whitespace-pre-wrap text-b16r text-gray6">
                      {postingDetailData?.content}
                    </p>
                  }
                />
                <SectionLabel
                  label="게시글 사진"
                  value={
                    <div className="flex flex-wrap gap-2 text-b16r text-gray6">
                      {postingDetailData?.communityMedias?.length || 0 > 0 ? (
                        <MultipleImagePreview
                          images={postingDetailData?.communityMedias || []}
                          width={128}
                          height={128}
                          isPreviewRatioFixed={false}
                          previewWidth={500}
                        />
                      ) : (
                        '-'
                      )}
                    </div>
                  }
                />
              </div>
              <div className="flex flex-col gap-6">
                <SectionLabel
                  label="지급 크레딧"
                  value={
                    postingDetailData?.communityActiveHistories?.creditValue
                      ? `[커뮤니티] 글 작성 (${Number(
                          postingDetailData?.communityActiveHistories
                            ?.creditValue || 0,
                        ).toLocaleString()}${UNIT_STRINGS.CREDIT})`
                      : '-'
                  }
                />
                <SectionLabel
                  label="조회 수"
                  value={`${formatter.NUM_COMMA(
                    postingDetailData?.communityViewHistoryCount,
                  )}회`}
                />
                <SectionLabel
                  label="댓글 수"
                  value={`${formatter.NUM_COMMA(
                    postingDetailData?.communityCommentCount,
                  )}회`}
                />
                <SectionLabel
                  label="좋아요 수"
                  value={`${formatter.NUM_COMMA(
                    postingDetailData?.communityLikeCount,
                  )}회`}
                />
                <SectionLabel
                  label="북마크 수"
                  value={`${formatter.NUM_COMMA(
                    postingDetailData?.communityBookmarkCount,
                  )}회`}
                />
                <SectionLabel
                  label="블라인드 일시"
                  value={
                    postingDetailData?.communityReport?.reportHandledAt
                      ? dayjs(
                          postingDetailData?.communityReport?.reportHandledAt,
                        ).format('YYYY-MM-DD HH:mm:ss')
                      : '-'
                  }
                />
                <SectionLabel
                  label="블라인드 사유"
                  value={
                    postingDetailData?.communityReport?.reportHandlingReason ||
                    '-'
                  }
                />
                <SectionLabel
                  label="블라인드 관리자"
                  value={
                    postingDetailData?.communityReport?.administrator?.name ||
                    '-'
                  }
                />
              </div>
            </div>
          </SectionCard>
          <SectionCard
            contentProps={{ className: 'p-0' }}
            cardTitle="댓글 정보"
          >
            <Table
              tableClassName="mt-4 h-auto flex-1 overflow-auto"
              containerClassName="flex h-[500px] flex-col"
              ref={gridRef}
              rowData={communityCommentData?.rows || []}
              isPaginationDataMapping
              columnDefs={communityPostingDetailCommentListColumn}
              page={page}
              pageSize={pageSize}
              total={communityCommentData?.count || 0}
              handlePaginationClick={handlePaginationClick}
            />
          </SectionCard>
        </>
      );
    } else {
      return (
        <>
          <SectionCard cardTitle="댓글 정보">
            <div className="grid grid-cols-[1fr_1fr] gap-6">
              <div className="flex flex-col gap-6">
                <SectionLabel
                  label="작성 일시"
                  value={formatter.DATE(commentDetailData?.createdAt)}
                />
                <SectionLabel
                  label="상태"
                  value={renderCommnutyReportTypeBadge()}
                />
                <SectionLabel
                  label="닉네임"
                  value={commentDetailData?.customer?.nick || '-'}
                />
                <SectionLabel
                  label="게시글 카테고리"
                  value={
                    commentDetailData?.communityPosting?.communityCategory
                      ?.categoryName || '-'
                  }
                />
                <SectionLabel
                  label="게시글 제목"
                  value={
                    <p className="whitespace-pre-wrap text-b16r text-gray6">
                      {commentDetailData?.communityPosting?.title}
                    </p>
                  }
                />
                <SectionLabel
                  label="댓글 내용"
                  value={
                    <p className="whitespace-pre-wrap text-b16r text-gray6">
                      {commentDetailData?.content}
                    </p>
                  }
                />
              </div>
              <div className="flex flex-col gap-6">
                <SectionLabel
                  label="지급 크레딧"
                  value={
                    commentDetailData?.communityActiveHistories?.creditValue
                      ? `[커뮤니티] 댓글 작성 (${Number(
                          commentDetailData?.communityActiveHistories
                            ?.creditValue || 0,
                        ).toLocaleString()}${UNIT_STRINGS.CREDIT})`
                      : '-'
                  }
                />
                <SectionLabel
                  label="좋아요 수"
                  value={`${formatter.NUM_COMMA(
                    postingDetailData?.communityLikeCount,
                  )}회`}
                />
                <SectionLabel
                  label="블라인드 일시"
                  value={
                    commentDetailData?.communityReport?.reportHandledAt
                      ? dayjs(
                          commentDetailData?.communityReport?.reportHandledAt,
                        ).format('YYYY-MM-DD HH:mm:ss')
                      : '-'
                  }
                />
                <SectionLabel
                  label="블라인드 사유"
                  value={
                    commentDetailData?.communityReport?.reportHandlingReason ||
                    '-'
                  }
                />
                <SectionLabel
                  label="블라인드 관리자"
                  value={
                    commentDetailData?.communityReport?.administrator?.name ||
                    '-'
                  }
                />
              </div>
            </div>
          </SectionCard>
        </>
      );
    }
  };

  // render
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={() => navigate(-1)}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
          <Button
            className="w-40 bg-red1"
            buttonSize="medium"
            buttonColor="red"
            onClick={() => {
              openDialog();
            }}
            disabled={
              !(
                postingDetailData?.communityReportType === 'common' ||
                commentDetailData?.communityReportType === 'common' ||
                postingDetailData?.communityReportType === 'reception' ||
                commentDetailData?.communityReportType === 'reception'
              )
            }
          >
            블라인드 처리
          </Button>
        </div>
      </BottomFixedContainer>
    );
  };

  return (
    <>
      <MainContentTitle
        breadcrumbList={[
          NAV_CONST.communityManagement,
          NAV_CONST.communityDetail,
        ]}
        pageTitle={NAV_CONST.communityDetail}
      />
      <div className="mb-[100px] flex flex-col gap-6">{renderContent()}</div>
      {renderBottomFixedContent()}
      <BlindModal
        id={Number(id)}
        blindType={isPosting ? 'posting' : 'comment'}
        onSubmit={refetchDetailData}
        open={isOpen}
        onOpenChange={value => {
          if (value) {
            openDialog();
          } else {
            closeDialog();
          }
        }}
      />
    </>
  );
}
